import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Image as ImageIcon, FolderOpen, ChevronLeft, Images, X, Download, ChevronRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const GaleriaPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const albumIdParam = searchParams.get('album');
  
  const [albums, setAlbums] = useState([]);
  const [fotos, setFotos] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(albumIdParam ? 'album' : 'albums');
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxPhotos, setLightboxPhotos] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [albumsRes, fotosRes] = await Promise.all([
        axios.get(`${API}/api/albums`),
        axios.get(`${API}/api/fotos`)
      ]);
      setAlbums(albumsRes.data || []);
      setFotos(fotosRes.data || []);
    } catch (error) {
      console.error("Error cargando datos:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAlbum = useCallback(async (albumId) => {
    try {
      const response = await axios.get(`${API}/api/albums/${albumId}`);
      setSelectedAlbum(response.data);
      setViewMode('album');
    } catch (error) {
      console.error("Error cargando álbum:", error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (albumIdParam) {
      fetchAlbum(albumIdParam);
    }
  }, [albumIdParam, fetchAlbum]);

  const openAlbum = (album) => {
    setSelectedAlbum(album);
    setViewMode('album');
    fetchAlbum(album.id);
  };

  const goBackToAlbums = () => {
    setSelectedAlbum(null);
    setViewMode('albums');
  };

  // Lightbox functions
  const openLightbox = (photo, photos, index) => {
    setLightboxPhoto(photo);
    setLightboxPhotos(photos);
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxPhoto(null);
    document.body.style.overflow = 'auto';
  };

  const nextPhoto = () => {
    const newIndex = (lightboxIndex + 1) % lightboxPhotos.length;
    setLightboxIndex(newIndex);
    setLightboxPhoto(lightboxPhotos[newIndex]);
  };

  const prevPhoto = () => {
    const newIndex = (lightboxIndex - 1 + lightboxPhotos.length) % lightboxPhotos.length;
    setLightboxIndex(newIndex);
    setLightboxPhoto(lightboxPhotos[newIndex]);
  };

  const downloadPhoto = async () => {
    if (!lightboxPhoto) return;
    try {
      const response = await fetch(lightboxPhoto.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${lightboxPhoto.titulo || 'foto'}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading:", error);
      // Fallback: open in new tab
      window.open(lightboxPhoto.url, '_blank');
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextPhoto();
      if (e.key === 'ArrowLeft') prevPhoto();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxIndex, lightboxPhotos]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-warm-bone">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-zuno-wine"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-bone py-12">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <Button
          onClick={() => navigate("/")}
          variant="ghost"
          className="mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2" size={20} />
          Volver al Inicio
        </Button>

        <div className="glass-card rounded-3xl p-8 md:p-12">
          {viewMode === 'albums' ? (
            <>
              <div className="flex items-center gap-4 mb-8">
                <Images className="h-12 w-12 text-zuno-wine" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold text-zuno-wine">
                    Galería de Fotos
                  </h1>
                  <p className="text-lg text-slate-600 mt-2">
                    Álbumes de nuestra generación
                  </p>
                </div>
              </div>

              {albums.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="h-24 w-24 mx-auto text-slate-300 mb-4" />
                  <p className="text-xl text-slate-600">No hay álbumes disponibles</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {albums.map((album, index) => (
                    <Card
                      key={album.id}
                      className="overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                      onClick={() => openAlbum(album)}
                      data-testid={`album-card-${index}`}
                    >
                      <div className="aspect-video relative bg-gradient-to-br from-purple-100 to-purple-200">
                        {album.portada_url ? (
                          <img
                            src={album.portada_url}
                            alt={album.titulo}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <FolderOpen className="h-16 w-16 text-purple-400" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                          <h3 className="font-bold text-xl">{album.titulo}</h3>
                          {album.descripcion && (
                            <p className="text-white/80 text-sm mt-1 line-clamp-2">{album.descripcion}</p>
                          )}
                          <p className="text-white/60 text-sm mt-2">{album.fotos_count || 0} fotos</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-4 mb-8">
                <Button
                  variant="ghost"
                  onClick={goBackToAlbums}
                  className="mr-2"
                  data-testid="back-to-albums"
                >
                  <ChevronLeft size={20} />
                  Volver
                </Button>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-zuno-wine">
                    {selectedAlbum?.titulo}
                  </h1>
                  {selectedAlbum?.descripcion && (
                    <p className="text-slate-600 mt-1">{selectedAlbum.descripcion}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {selectedAlbum?.fotos && selectedAlbum.fotos.length > 0 ? (
                  selectedAlbum.fotos.map((foto, index) => (
                    <div
                      key={foto.id}
                      className="relative overflow-hidden rounded-xl group cursor-pointer aspect-square"
                      onClick={() => openLightbox(foto, selectedAlbum.fotos, index)}
                      data-testid={`album-foto-${index}`}
                    >
                      <img
                        src={foto.url}
                        alt={foto.titulo}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        style={{ 
                          transform: `rotate(${foto.rotation || 0}deg)`,
                          objectFit: 'cover'
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                        <p className="text-white font-semibold text-sm truncate">{foto.titulo}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <ImageIcon className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-600">Este álbum no tiene fotos</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && lightboxPhoto && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
            onClick={closeLightbox}
            data-testid="lightbox-close"
          >
            <X size={32} />
          </button>

          {/* Download button */}
          <button
            className="absolute top-4 left-4 text-white hover:text-gray-300 z-50 flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg"
            onClick={(e) => { e.stopPropagation(); downloadPhoto(); }}
            data-testid="lightbox-download"
          >
            <Download size={20} />
            Descargar
          </button>

          {/* Navigation arrows */}
          {lightboxPhotos.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 p-3 rounded-full z-50"
                onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={32} />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 p-3 rounded-full z-50"
                onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                data-testid="lightbox-next"
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}

          {/* Photo */}
          <div 
            className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxPhoto.url}
              alt={lightboxPhoto.titulo}
              className="max-w-full max-h-[85vh] object-contain"
              style={{ transform: `rotate(${lightboxPhoto.rotation || 0}deg)` }}
            />
          </div>

          {/* Photo info */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-center z-50">
            <p className="font-semibold text-lg">{lightboxPhoto.titulo}</p>
            {lightboxPhoto.descripcion && (
              <p className="text-white/70 text-sm mt-1">{lightboxPhoto.descripcion}</p>
            )}
            <p className="text-white/50 text-xs mt-2">
              {lightboxIndex + 1} / {lightboxPhotos.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GaleriaPage;
